import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const KEY = "pula_cookie_consent";
const VERSION = "2026-07-24";
const CookieConsentContext = createContext(null);
export const useCookieConsent = () => useContext(CookieConsentContext);

const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } };

function PrefRow({ title, desc, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 pula-card p-3">
      <div>
        <p className="font-heading font-bold text-sm text-pula-ink">{title}</p>
        <p className="text-xs text-pula-inksoft font-body mt-0.5">{desc}</p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
        <input type="checkbox" data-testid={testId} checked={checked} disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)} className="sr-only peer" />
        <div className={`w-10 h-6 rounded-full transition-colors ${checked ? "bg-pula-green" : "bg-pula-bg2 border border-[var(--pula-border)]"} peer-disabled:opacity-60`} />
        <div className={`absolute left-1 top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
      </label>
    </div>
  );
}

export function CookieConsentProvider({ children }) {
  const location = useLocation();
  const chatRoutes = ["/chat", "/messages", "/confidante"];
  const onChat = chatRoutes.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  const [consent, setConsent] = useState(() => read());
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [draft, setDraft] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const c = read();
    if (!c || c.version !== VERSION) setBannerOpen(true);
    else setDraft({ analytics: !!c.analytics, marketing: !!c.marketing });
  }, []);

  const save = (choice) => {
    const value = { essential: true, analytics: !!choice.analytics, marketing: !!choice.marketing, version: VERSION, ts: new Date().toISOString() };
    localStorage.setItem(KEY, JSON.stringify(value));
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        analytics_storage: value.analytics ? "granted" : "denied",
        ad_storage: value.marketing ? "granted" : "denied",
        ad_user_data: value.marketing ? "granted" : "denied",
        ad_personalization: value.marketing ? "granted" : "denied",
      });
    }
    setConsent(value);
    setBannerOpen(false);
    setPrefsOpen(false);
  };

  const openCookiePreferences = useCallback(() => {
    const c = read();
    setDraft({ analytics: !!c?.analytics, marketing: !!c?.marketing });
    setPrefsOpen(true);
  }, []);

  return (
    <CookieConsentContext.Provider value={{ consent, openCookiePreferences }}>
      {children}
      {bannerOpen && !onChat && (
        <div data-testid="cookie-banner" className="fixed bottom-0 inset-x-0 z-[60] bg-pula-ink text-white border-t border-pula-green/30 shadow-2xl">
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <p className="text-xs sm:text-sm font-body text-white/85 flex items-center gap-2 flex-1 min-w-0">
              <Cookie className="w-4 h-4 shrink-0 text-pula-green" />
              <span>We use essential cookies to keep Pula working — we never sell your data. <Link to="/market/cookies" className="underline hover:text-pula-gold">Cookie Notice</Link>.</span>
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Button data-testid="cookie-accept-all" onClick={() => save({ analytics: true, marketing: true })} className="rounded-full bg-pula-green hover:bg-pula-greenhover text-white h-9 px-4 text-sm">Accept all</Button>
              <Button data-testid="cookie-essential-only" onClick={() => save({ analytics: false, marketing: false })} className="rounded-full bg-white/10 hover:bg-white/20 text-white border-0 h-9 px-4 text-sm">Essential only</Button>
              <button data-testid="cookie-preferences-open" onClick={() => setPrefsOpen(true)} className="text-xs text-white/70 hover:text-white underline whitespace-nowrap">Preferences</button>
            </div>
          </div>
        </div>
      )}
      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent data-testid="cookie-prefs-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Cookie className="w-5 h-5 text-pula-green" /> Cookie preferences</DialogTitle>
            <DialogDescription>Choose which optional cookies you allow. Essential cookies are always on so the site works.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <PrefRow title="Essential" desc="Sign-in, security and your saved settings. Always on." checked disabled />
            <PrefRow title="Analytics" desc="Helps us understand usage so we can improve Pula." checked={draft.analytics} onChange={(v) => setDraft((d) => ({ ...d, analytics: v }))} testId="cookie-toggle-analytics" />
            <PrefRow title="Marketing" desc="Not used yet — reserved for the future, only ever with your consent." checked={draft.marketing} onChange={(v) => setDraft((d) => ({ ...d, marketing: v }))} testId="cookie-toggle-marketing" />
          </div>
          <DialogFooter className="gap-2">
            <Button data-testid="cookie-reject-optional" variant="outline" onClick={() => save({ analytics: false, marketing: false })} className="rounded-full border-pula-ink/20">Reject optional</Button>
            <Button data-testid="cookie-save-prefs" onClick={() => save(draft)} className="rounded-full bg-pula-green hover:bg-pula-greenhover text-white">Save preferences</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CookieConsentContext.Provider>
  );
}
